import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, MessageSquare, Headphones, Calendar, BookOpen, Clock } from "lucide-react";
import { CanvasRevealEffect } from "../ui/canvas-reveal-effect";
import { STAR } from "../../constants/imageConstants";
import { useAIAgentData } from "../../hooks/useAIAgentData";

// Icon mapping dictionary to resolve string names to Lucide icon components
const iconMap: Record<string, React.ComponentType<any>> = {
  Target,
  MessageSquare,
  Headphones,
  Calendar,
  BookOpen,
  Clock,
};

function renderIcon(name: string, size: number, className?: string) {
  const Icon = iconMap[name];
  return Icon ? <Icon size={size} className={className} /> : null;
}

// Shared animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12 },
  }),
} as const;

// BOT-style card helper
const bgSvg = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 375 314" preserveAspectRatio="none"><g transform="matrix(-0.92213 41.54 -49.609 -11.223 187.5 157)"><foreignObject x="-72.78" y="-72.78" width="145.56" height="145.56"><div xmlns="http://www.w3.org/1999/xhtml" style="background-image: conic-gradient(from 90deg, rgba(71, 47, 140, 0.4) 0%, rgba(54, 35, 105, 0.3) 11.991%, rgba(36, 23, 70, 0.2) 23.981%, rgba(0, 0, 0, 0) 47.962%, rgba(18, 13, 32, 0.25) 55.503%, rgba(37, 26, 65, 0.5) 63.044%, rgba(55, 40, 97, 0.75) 70.584%, rgba(73, 53, 130, 1) 78.125%, rgba(71, 47, 140, 0.4) 100%); opacity:0.3; height: 100%; width: 100%;"/></foreignObject></g></svg>`;

function AgentCard({ title, description, icon }: { title: string; description: string; icon: React.ReactNode }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="bg-white/10 backdrop-blur-sm cursor-pointer rounded-3xl min-h-52 border border-[#6A5555]/40 px-4 py-6 lg:p-6 relative w-full"
      style={{ backgroundImage: `url('${bgSvg}')` }}
    >
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="h-full w-full absolute inset-0 rounded-3xl overflow-hidden"
          >
            <CanvasRevealEffect animationSpeed={5.1} containerClassName="bg-[#147af8]" />
            <div className="absolute inset-0 [mask-image:radial-gradient(400px_at_center,white,transparent)] bg-black/50 dark:bg-black/90" />
          </motion.div>
        )}
      </AnimatePresence>
      <img className="absolute top-14 left-10 size-4 object-cover" src={STAR} alt="" />
      <img className="absolute top-14 right-10 size-4 object-cover" src={STAR} alt="" />
      <div className="flex flex-col justify-center items-center space-y-3 relative z-10 pt-8">
        <div className="absolute -top-14 w-14 h-14 bg-gradient-to-b from-[#3982fd] to-[#bc98ff] rounded-full flex items-center justify-center border border-[#cb97ff] text-white">
          {icon}
        </div>
        <h3 className="text-xl lg:text-2xl text-white text-center">{title}</h3>
        <p className="text-base text-white/80 leading-relaxed text-center">{description}</p>
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  const { data } = useAIAgentData();
  const { badgeText, title, description, features, tailoredTitle, tailoredDescription } = data.featuresSection;

  return (
    <section className="relative px-5 sm:px-10 md:px-16 sm:py-16 py-8">
      <div className="space-y-6 mb-20 text-center">
        <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-[#A6A0F6] text-sm font-medium tracking-widest uppercase">
          {badgeText}
        </motion.p>
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="text-4xl md:text-6xl font-bold text-white">
          {title}
        </motion.h2>
        <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} className="text-2xl text-[#d9d9d9] max-w-3xl mx-auto leading-relaxed">
          {description}
        </motion.p>
      </div>

      {/* Desktop layout — matches BOTModelSection exactly */}
      <div className="hidden lg:flex gap-20 justify-center items-center flex-wrap pt-10">
        {features.map((f, i) => (
          <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} className="w-full max-w-md mx-auto">
            <AgentCard title={f.title} description={f.desc} icon={renderIcon(f.iconName, 32)} />
          </motion.div>
        ))}
      </div>

      {/* Mobile layout */}
      <div className="block lg:hidden space-y-20 pt-10">
        {features.map((f, i) => (
          <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
            <AgentCard title={f.title} description={f.desc} icon={renderIcon(f.iconName, 32)} />
          </motion.div>
        ))}
      </div>

      {/* Tailored statement — inline, no box */}
      <motion.div
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="mt-24 text-center space-y-4"
      >
        <h3 className="text-3xl md:text-5xl font-bold text-white">{tailoredTitle}</h3>
        <p className="text-2xl text-[#d9d9d9] max-w-3xl mx-auto leading-relaxed">
          {tailoredDescription}
        </p>
      </motion.div>
    </section>
  );
}
